import { DestroyRef, inject, Injectable } from '@angular/core';
import { getApiUrl } from '@app/core/services/utils/util.service';
import { BeforeSendResult, CoralogixRum, CxStackFrame, EditableCxRumEvent } from '@coralogix/browser';
import { environment } from '@environment/environment';
import { context, Span, trace } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { first } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { UserService } from '../user/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const INTERNAL_EMAIL_DOMAINS = ['@prettydamnquick.com', '@pdq.team'];
const MAXX_EXTERNAL_USER_TRIGGER = 'external_user_maxx';
const ALWAYS_RECORD_SHOP_TRIGGER = 'always_record_shop';
const NETWORK_FAILURE_STATUS_CODE = 0;
const BROWSER_EXTENSION_URL_PATTERN = /^(chrome|moz|safari-web)-extension:\/\//;
const IGNORED_RUM_ERRORS = [
  /^Unleash: unable to fetch feature toggles/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
  /Incorrect email or password/,
];

function isBrowserExtensionFrame(frame: CxStackFrame): boolean {
  return BROWSER_EXTENSION_URL_PATTERN.test(frame.fileName ?? '');
}

function isBrowserExtensionError(event: EditableCxRumEvent): boolean {
  const frames = event.error_context?.original_stacktrace ?? [];
  return frames.length > 0 && frames.every(isBrowserExtensionFrame);
}

function isNetworkFailureWithoutResponse(event: EditableCxRumEvent): boolean {
  return event.network_request_context?.status_code === NETWORK_FAILURE_STATUS_CODE;
}

function dropRumNoise(event: EditableCxRumEvent): BeforeSendResult {
  if (isBrowserExtensionError(event) || isNetworkFailureWithoutResponse(event)) {
    return null;
  }
  return event;
}

@Injectable({
  providedIn: 'root',
})
export class TracingService {
  private initialized = false;
  private readonly tracer;
  private rootOtelSpan: Span | null = null;
  private readonly sessionId: string;
  private readonly sessionStartTime: number;
  private readonly tracingURL = environment.tracingUrl || 'http://localhost:4200/v1/traces';
  private readonly userService: UserService = inject(UserService);
  private readonly isLocal = environment.local;
  private destroyRef = inject(DestroyRef);
  private currentUser: any = null;
  private isCoralogixRUM: boolean;

  // ========== Initialization Methods ==========
  constructor() {
    if (!this.initialized) {
      this.initialized = true;
      this.isCoralogixRUM = !!environment.CORALOGIX_RUM_API_KEY;
      this.sessionId = uuidv4();
      this.sessionStartTime = Date.now();

      if (this.isLocal || this.isCoralogixRUM) {
        console.log('🔭 Initializing TracingService...');

        if (this.isCoralogixRUM) {
          this.initRum();
          this.tracer = CoralogixRum.getCustomTracer();
        } else {
          this.initializeTracerProvider();
          this.tracer = trace.getTracer('frontend', '1.0.0');
        }

        console.log('🔭 Tracer initialized');

        this.initializeRootSpan();

        this.userService.currentPdqUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
          if (user) {
            if (this.isCoralogixRUM) {
              this.setUserContext(user);
            } else {
              this.updateUserAttributes(user);
            }
          }
        });
      } else {
        console.log('TracingService initialized but tracing disabled');
      }
    }
  }

  private initRum(): void {
    if (environment.CORALOGIX_RUM_API_KEY) {
      CoralogixRum.init({
        public_key: environment.CORALOGIX_RUM_API_KEY,
        application: 'frontend-RUM',
        version: '0.0.8',
        coralogixDomain: 'US2',
        ignoreErrors: IGNORED_RUM_ERRORS,
        beforeSend: dropRumNoise,
        traceParentInHeader: {
          enabled: true,
          options: {
            propagateTraceHeaderCorsUrls: [new RegExp(getApiUrl())],
          },
        },
        sessionRecordingConfig: {
          enable: true,
          // Nothing records until startSessionRecording() is called explicitly.
          autoStartSessionRecording: false,
          recordConsoleEvents: true,
          sessionRecordingSampleRate: 100,
          maxMutations: 5000,
        },
        // memoryUsageConfig: {
        //   enabled: true,
        //   interval: 60_000,
        // },
      });
      console.log('Coralogix RUM initialized');
    }
  }

  private setUserContext(user: any) {
    if (user && this.isCoralogixRUM) {
      CoralogixRum.setUserContext({
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
      });
      CoralogixRum.setLabels({
        ...CoralogixRum.getLabels(),
        environment: environment.env,
        shopId: +user.tenantId,
      });
    }
    if (environment.env === 'prod' && +user.tenantId === 2542) {
      CoralogixRum.setLabels({ ...CoralogixRum.getLabels(), sessionRecordingTrigger: ALWAYS_RECORD_SHOP_TRIGGER });
      CoralogixRum.startSessionRecording();
    }
  }

  private initializeTracerProvider(): void {
    const processors = [];

    if (this.isLocal) {
      console.log('Initializing TracerProvider for tracingURL:', this.tracingURL);
      const otlpExporter = new OTLPTraceExporter({
        url: this.tracingURL,
      });
      processors.push(
        new BatchSpanProcessor(otlpExporter, {
          maxQueueSize: 100,
          scheduledDelayMillis: 500,
          exportTimeoutMillis: 30000,
        }),
      );
      const provider = new WebTracerProvider({
        resource: new Resource({
          'service.name': 'frontend',
          'service.version': '1.0.0',
          'browser.user_agent': navigator.userAgent,
          'browser.language': navigator.language,
          'deployment.environment': environment.env,
        }),
        spanProcessors: processors,
      });
      provider.register();
    }
  }

  private initializeRootSpan(): void {
    if (!this.tracer) {
      console.warn('Tracer not initialized, skipping root span creation');
      return;
    }

    const coralogixSessionId = window['CoralogixRum']?.getSessionId?.() || 'not-available';
    const ctx = context.active();

    const attributes = {
      'session.id': this.sessionId,
      'coralogix.session.id': coralogixSessionId,
      'browser.url': window.location.href,
      'browser.page_title': document.title,
      'session.start_time': new Date(this.sessionStartTime).toISOString(),
    };

    try {
      if (!this.isCoralogixRUM) {
        // Use standard OpenTelemetry tracer for local development
        this.rootOtelSpan = this.tracer.startSpan('session', { attributes }, ctx);
      }
    } catch (error) {
      console.error('Failed to initialize root span:', error);
    }
  }

  startSessionRecordingForExternalUsers(): void {
    if (!this.isCoralogixRUM) {
      return;
    }
    this.userService.currentPdqUser$
      .pipe(
        first((user) => !!user),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        const email = user.email?.toLowerCase() || '';
        const isInternalUser = INTERNAL_EMAIL_DOMAINS.some((domain) => email.endsWith(domain));
        if (isInternalUser) {
          return;
        }
        CoralogixRum.setLabels({ ...CoralogixRum.getLabels(), sessionRecordingTrigger: MAXX_EXTERNAL_USER_TRIGGER });
        CoralogixRum.startSessionRecording();
      });
  }

  // ========== Span Management Methods ==========
  startSpan(name: string, attributes: Record<string, any> = {}): any {
    const userAttributes = this.currentUser
      ? {
          'user.name': this.currentUser.name,
          'user.email': this.currentUser.email,
        }
      : {};

    const mergedAttributes = {
      ...attributes,
      ...userAttributes,
      'session.id': this.sessionId || 'unknown',
    };

    try {
      if (!this.isCoralogixRUM && this.tracer) {
        const activeContext = context.active();
        const activeSpan = trace.getSpan(activeContext);
        const ctx = activeSpan
          ? activeContext
          : this.rootOtelSpan
            ? trace.setSpan(activeContext, this.rootOtelSpan)
            : activeContext;
        return this.tracer.startSpan(
          name,
          {
            attributes: mergedAttributes,
          },
          ctx,
        );
      }
      console.warn('Failed to create span - no tracer available');
      throw new Error('No tracer available');
    } catch (error) {
      console.error('Error creating span:', error);
      throw error;
    }
  }

  endSession(): void {
    if (this.rootOtelSpan) {
      const duration = Date.now() - this.sessionStartTime;
      console.log(`Ending session. Duration: ${duration}ms`);
      this.rootOtelSpan.setAttributes({
        'session.duration_ms': duration,
        'session.end_time': new Date().toISOString(),
      });
      this.rootOtelSpan.end();
      this.rootOtelSpan = null;
    }
  }

  // ========== Utility Methods ==========

  private updateUserAttributes(user: any): void {
    if (user) {
      this.currentUser = user;
      const attributes = {
        'user.name': user.name,
        'user.email': user.email,
        shopId: +user.tenantId,
      };
      {
        if (this.rootOtelSpan) {
          this.rootOtelSpan.setAttributes(attributes);
        }
      }
    }
  }
}
